import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import { PendingBookingActions } from './PendingBookingActions';
import { ReviewIdentityDialog } from './ReviewIdentityDialog';

export interface BookingRequestItem {
  bookingProspectId: string;
  appointmentId?: string | null;
  appointmentStatus?: string | null;
  appointmentStartTime?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  reason?: string | null;
}

export interface BookingRequestsPanelProps {
  open: boolean;
  onClose: () => void;
  items: BookingRequestItem[];
  isLoading?: boolean;
  /** Subject-language plural, e.g. "patients" / "clients". */
  subjectPlural: string;
}

function formatStart(startTime?: string | null): string {
  if (!startTime) return 'Time unavailable';
  const date = new Date(startTime);
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Slide-over list of pending self-serve booking requests (PRD D1).
 *
 * Each row is a booking-request card — deliberately not a patient chart
 * (ADR-0077: the booker is a quarantined prospect until staff promote them).
 * Confirm/decline act on the appointment; identity review is a separate step.
 */
export function BookingRequestsPanel({
  open,
  onClose,
  items,
  isLoading = false,
  subjectPlural,
}: BookingRequestsPanelProps) {
  const [reviewProspectId, setReviewProspectId] = useState<string | null>(null);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Booking requests
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Self-booked appointments waiting on your review
          </Typography>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="Close booking requests">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={110} />
            <Skeleton variant="rounded" height={110} />
          </Stack>
        ) : items.length === 0 ? (
          <Stack spacing={1} sx={{ alignItems: 'center', py: 6, textAlign: 'center' }}>
            <EventBusyOutlinedIcon color="disabled" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              No pending requests — new {subjectPlural} who book online will
              show up here.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {items.map(item => {
              const name =
                [item.firstName, item.lastName].filter(Boolean).join(' ') ||
                'Unknown requester';
              const confirmed = item.appointmentStatus === 'scheduled';
              return (
                <Box
                  key={item.bookingProspectId}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {name}
                    </Typography>
                    <Chip
                      size="small"
                      label={confirmed ? 'Confirmed' : 'Pending'}
                      color={confirmed ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatStart(item.appointmentStartTime)}
                  </Typography>
                  {(item.email || item.phone) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      {[item.email, item.phone].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  {item.reason && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontStyle: 'italic' }}
                    >
                      “{item.reason}”
                    </Typography>
                  )}
                  <Box sx={{ mt: 1.5 }}>
                    {!confirmed ? (
                      <PendingBookingActions
                        appointmentId={item.appointmentId}
                        bookingProspectId={item.bookingProspectId}
                        source="queue"
                        compact
                        onReviewIdentity={() =>
                          setReviewProspectId(item.bookingProspectId)
                        }
                      />
                    ) : (
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<PersonSearchOutlinedIcon />}
                        onClick={() =>
                          setReviewProspectId(item.bookingProspectId)
                        }
                      >
                        Review identity
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
      {reviewProspectId && (
        <ReviewIdentityDialog
          bookingProspectId={reviewProspectId}
          open
          onClose={() => setReviewProspectId(null)}
          source="queue"
        />
      )}
    </Drawer>
  );
}
